import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    color: '#e2e8f0',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  };

  const contentStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    background: 'rgba(30, 41, 59, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: '1px solid rgba(148, 163, 184, 0.2)'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '40px',
    paddingBottom: '30px',
    borderBottom: '2px solid rgba(34, 211, 238, 0.3)'
  };

  const titleStyle = {
    fontSize: '42px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '10px'
  };

  const subtitleStyle = {
    fontSize: '16px',
    color: '#94a3b8',
    marginTop: '10px'
  };

  const sectionStyle = {
    marginBottom: '40px'
  };

  const h2Style = {
    fontSize: '28px',
    fontWeight: '600',
    color: '#22d3ee',
    marginTop: '40px',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid rgba(34, 211, 238, 0.2)'
  };

  const h3Style = {
    fontSize: '22px',
    fontWeight: '600',
    color: '#60a5fa',
    marginTop: '28px',
    marginBottom: '12px'
  };

  const h4Style = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#7dd3fc',
    marginTop: '20px',
    marginBottom: '10px'
  };

  const pStyle = {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#cbd5e1',
    marginBottom: '16px'
  };

  const ulStyle = {
    marginLeft: '24px',
    marginBottom: '16px',
    color: '#cbd5e1'
  };

  const liStyle = {
    marginBottom: '8px',
    lineHeight: '1.7'
  };

  const warningBoxStyle = {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '2px solid rgba(239, 68, 68, 0.4)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    marginTop: '24px'
  };

  const warningTitleStyle = {
    color: '#fca5a5',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const infoBoxStyle = {
    background: 'rgba(59, 130, 246, 0.1)',
    border: '2px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    marginTop: '24px'
  };

  const successBoxStyle = {
    background: 'rgba(34, 197, 94, 0.1)',
    border: '2px solid rgba(34, 197, 94, 0.3)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    marginTop: '24px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px',
    marginBottom: '20px',
    fontSize: '15px'
  };

  const thStyle = {
    background: 'rgba(59, 130, 246, 0.2)',
    padding: '12px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#22d3ee',
    borderBottom: '2px solid rgba(59, 130, 246, 0.4)'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
    color: '#cbd5e1'
  };

  const buttonStyle = {
    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '30px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Terms of Service</h1>
          <p style={subtitleStyle}>
            <strong>Effective Date:</strong> January 20, 2026<br />
            <strong>Last Updated:</strong> January 20, 2026<br />
            <strong>Version:</strong> 1.0
          </p>
        </div>

        <section style={sectionStyle}>
          <h2 style={h2Style}>Introduction</h2>
          <p style={pStyle}>
            These Terms of Service ("<strong>Terms</strong>", "<strong>Agreement</strong>") govern your access to and use 
            of the AppleTree ESG Platform, including our website, application, services, and certification system 
            (collectively, the "<strong>Platform</strong>" or "<strong>Service</strong>").
          </p>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>⚠️</span> Important Agreement
            </div>
            <p style={{...pStyle, marginBottom: 0}}>
              <strong>By accessing or using the Platform, you agree to be bound by these Terms.</strong> If you do not 
              agree to these Terms, you may not use the Platform.
            </p>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>1. Definitions</h2>
          <ul style={ulStyle}>
            <li style={liStyle}>
              <strong>"AppleTree", "we", "us", "our":</strong> Refers to the AppleTree ESG Platform, currently operated 
              by Armando Dragoti (Founder), with plans to incorporate within the European Union.
            </li>
            <li style={liStyle}>
              <strong>"Company", "you", "your":</strong> The business entity or organization submitting ESG questionnaires 
              and requesting certification through the Platform.
            </li>
            <li style={liStyle}>
              <strong>"User":</strong> Any individual or entity accessing or using the Platform.
            </li>
            <li style={liStyle}>
              <strong>"Certification":</strong> The ESG certification outcome issued by AppleTree (Approved / Rejected / 
              Revoked / Expired).
            </li>
            <li style={liStyle}>
              <strong>"On-chain":</strong> Data or transactions recorded on a public blockchain (e.g., Arbitrum, Ethereum) 
              via smart contracts.
            </li>
            <li style={liStyle}>
              <strong>"IPFS":</strong> InterPlanetary File System, a decentralized storage protocol used to publish Reports.
            </li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>2. Acceptance of Terms</h2>
          <h3 style={h3Style}>2.1 Binding Agreement</h3>
          <p style={pStyle}>By:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Accessing the Platform</li>
            <li style={liStyle}>Creating an account</li>
            <li style={liStyle}>Connecting your Wallet</li>
            <li style={liStyle}>Submitting an ESG questionnaire</li>
            <li style={liStyle}>Requesting certification</li>
          </ul>
          <p style={pStyle}>
            You agree to be legally bound by these Terms, our Privacy Policy, and any additional policies referenced herein.
          </p>

          <h3 style={h3Style}>2.2 Authority to Agree</h3>
          <p style={pStyle}>You represent and warrant that:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>You are at least 18 years of age</li>
            <li style={liStyle}>You are an <strong>Authorized Representative</strong> with full legal capacity to bind the Company</li>
            <li style={liStyle}>You have obtained all necessary internal approvals</li>
            <li style={liStyle}>You are not prohibited by law from using the Platform</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>3. Description of Service</h2>
          <h3 style={h3Style}>3.1 What AppleTree Provides</h3>
          <p style={pStyle}>AppleTree offers a <strong>digital ESG certification platform</strong> that:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Enables Companies to submit ESG self-assessments</li>
            <li style={liStyle}>Evaluates submissions based on a proprietary scoring methodology</li>
            <li style={liStyle}>Issues blockchain-based certifications for approved submissions</li>
            <li style={liStyle}>Publishes transparent certification data via IPFS and public blockchains</li>
            <li style={liStyle}>Displays certified Companies on a public map and registry</li>
          </ul>

          <h3 style={h3Style}>3.2 What AppleTree Is NOT</h3>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>❌</span> Important Disclaimers
            </div>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}><strong>Not a Legal Audit:</strong> AppleTree certifications are not legal, financial, or compliance audits.</li>
              <li style={liStyle}><strong>Not Regulatory Compliance:</strong> Certification does not guarantee compliance with laws.</li>
              <li style={liStyle}><strong>Not Investment Advice:</strong> Reports do not constitute investment recommendations.</li>
              <li style={liStyle}><strong>Not a Guarantee:</strong> Certification does not guarantee future ESG performance.</li>
              <li style={liStyle}><strong>Not Liability Insurance:</strong> AppleTree assumes no liability for certified Companies' actions.</li>
            </ul>
          </div>

          <h3 style={h3Style}>3.3 Beta/Early-Stage Disclaimer</h3>
          <p style={pStyle}>The Platform is in an <strong>early development stage</strong>:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Features may change without notice</li>
            <li style={liStyle}>Bugs or errors may occur</li>
            <li style={liStyle}>Scoring methodology may be refined</li>
            <li style={liStyle}>Service may be interrupted for maintenance</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>4. Certification Process</h2>
          <h3 style={h3Style}>4.1 Submission Phase</h3>
          <h4 style={h4Style}>Required Steps:</h4>
          <ul style={ulStyle}>
            <li style={liStyle}>Answer all mandatory questions truthfully and accurately</li>
            <li style={liStyle}>Provide supporting documentation (policies, certificates, reports)</li>
            <li style={liStyle}>Select map visibility preference (exact / city / country / none)</li>
            <li style={liStyle}>Agree to Terms of Service and Privacy Policy</li>
            <li style={liStyle}>Consent to public disclosure (if certified)</li>
          </ul>

          <h3 style={h3Style}>4.2 Review Phase</h3>
          <div style={infoBoxStyle}>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '8px'}}>Review Timeline:</p>
            <p style={{...pStyle, marginBottom: '8px'}}>• <strong>Standard review:</strong> 5-10 business days</p>
            <p style={{...pStyle, marginBottom: '8px'}}>• <strong>Complex cases:</strong> Up to 20 business days</p>
            <p style={{...pStyle, marginBottom: 0}}>• <strong>Expedited review:</strong> Available upon request (additional fee may apply)</p>
          </div>

          <h3 style={h3Style}>4.3 Certification Decision</h3>
          <p style={pStyle}>AppleTree may:</p>
          <ul style={ulStyle}>
            <li style={liStyle}><strong>Approve Certification:</strong> ESG scores calculated, NFT minted, report published to IPFS</li>
            <li style={liStyle}><strong>Reject Certification:</strong> Due to incomplete info, insufficient evidence, or policy violations</li>
            <li style={liStyle}><strong>Request Revisions:</strong> Ask for corrections or additional documentation</li>
          </ul>

          <h3 style={h3Style}>4.4 Certification Validity</h3>
          <ul style={ulStyle}>
            <li style={liStyle}><strong>Standard validity:</strong> 12 months from issue date</li>
            <li style={liStyle}><strong>Renewal:</strong> Companies may reapply before expiration</li>
            <li style={liStyle}><strong>Revocation:</strong> May occur if false information is discovered or Terms are violated</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>5. Company Obligations</h2>
          <h3 style={h3Style}>5.1 Truthfulness and Accuracy</h3>
          <p style={pStyle}>You represent and warrant that:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>All information provided is <strong>true, accurate, complete, and current</strong></li>
            <li style={liStyle}>All uploaded documents are <strong>authentic and unaltered</strong></li>
            <li style={liStyle}>You have <strong>not omitted material information</strong> relevant to ESG assessment</li>
            <li style={liStyle}>Responses reflect your <strong>actual ESG practices</strong>, not aspirations</li>
          </ul>

          <h3 style={h3Style}>5.2 Legal Compliance</h3>
          <p style={pStyle}>You represent that the Company:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Is in good standing in its jurisdiction</li>
            <li style={liStyle}>Complies with all applicable labor, environmental, and anti-corruption laws</li>
            <li style={liStyle}>Has obtained all necessary permits and licenses</li>
            <li style={liStyle}>Does not engage in illegal activities (child labor, forced labor, etc.)</li>
          </ul>

          <h3 style={h3Style}>5.3 Data Privacy and Consent</h3>
          <p style={pStyle}>You represent that:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>You have lawful authority to provide personal data included in submissions</li>
            <li style={liStyle}>You have obtained necessary consents from individuals whose data is submitted</li>
            <li style={liStyle}>You comply with GDPR and applicable privacy laws</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>6. Prohibited Activities</h2>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>🚫</span> You May NOT:
            </div>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}>Attempt to hack, reverse engineer, or circumvent security measures</li>
              <li style={liStyle}>Submit false, misleading, or fabricated information</li>
              <li style={liStyle}>Upload forged or altered documents</li>
              <li style={liStyle}>Create duplicate accounts to manipulate outcomes</li>
              <li style={liStyle}>Upload malware, viruses, or malicious code</li>
              <li style={liStyle}>Use the Platform to facilitate illegal activities</li>
              <li style={liStyle}>Violate intellectual property rights</li>
            </ul>
          </div>
          <p style={pStyle}>
            <strong>Violation may result in:</strong> Immediate account suspension, certification revocation, 
            legal action, and permanent ban from the Platform.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>7. Transparency, IPFS, and Blockchain</h2>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>⚠️</span> CRITICAL: By Requesting Certification, You Accept:
            </div>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}>
                <strong>Public Disclosure:</strong> Approved certifications will be publicly visible on AppleTree's 
                website, public map, and registry
              </li>
              <li style={liStyle}>
                <strong>IPFS Publication:</strong> Reports may be published to IPFS (decentralized storage) and may 
                persist even if unpinned
              </li>
              <li style={liStyle}>
                <strong>Blockchain Immutability:</strong> Certifications recorded on blockchain cannot be deleted or altered
              </li>
              <li style={liStyle}>
                <strong>Limited Right to Erasure:</strong> AppleTree cannot fully erase blockchain transactions or 
                IPFS content from all nodes
              </li>
            </ul>
          </div>

          <div style={infoBoxStyle}>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '8px'}}>What We CAN Do Upon Deletion Request:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>✅ Remove data from our databases</li>
              <li style={liStyle}>✅ Unpin IPFS content</li>
              <li style={liStyle}>✅ Remove links from AppleTree website</li>
              <li style={liStyle}>✅ Revoke certification status on-chain</li>
            </ul>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '8px', marginTop: '16px'}}>What We CANNOT Guarantee:</p>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}>❌ Full deletion from all IPFS nodes</li>
              <li style={liStyle}>❌ Deletion of blockchain transactions</li>
              <li style={liStyle}>❌ Removal from blockchain explorers</li>
            </ul>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>8. Intellectual Property</h2>
          <h3 style={h3Style}>8.1 AppleTree's IP</h3>
          <p style={pStyle}>
            All Platform content, features, and materials are owned by AppleTree or its licensors, including 
            trademarks, software, scoring methodologies, and report templates.
          </p>

          <h3 style={h3Style}>8.2 Your Submissions (License Grant)</h3>
          <p style={pStyle}>
            By submitting documents and information, you grant AppleTree a non-exclusive, worldwide, royalty-free 
            license to use, reproduce, modify, and distribute your submission for verification, certification, 
            report generation, and public disclosure purposes.
          </p>

          <h3 style={h3Style}>8.3 Certification Mark Usage</h3>
          <div style={successBoxStyle}>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '8px'}}>If Certified, You May:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>✅ Display the "AppleTree Certified" badge on your website</li>
              <li style={liStyle}>✅ Reference your ESG scores in marketing materials</li>
              <li style={liStyle}>✅ Include certification in sustainability reports</li>
            </ul>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '8px', marginTop: '16px'}}>You May NOT:</p>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}>❌ Use the badge if certification is expired or revoked</li>
              <li style={liStyle}>❌ Imply endorsement beyond ESG certification</li>
              <li style={liStyle}>❌ Modify the badge design without permission</li>
            </ul>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>9. Payment and Fees</h2>
          <h3 style={h3Style}>9.1 Current Pricing</h3>
          <p style={pStyle}>
            During the beta phase, certification services are <strong>currently free of charge</strong>. 
            This is subject to change with at least 30 days' advance notice.
          </p>

          <h3 style={h3Style}>9.2 Refund Policy</h3>
          <p style={pStyle}>
            <strong>Fees are non-refundable</strong> except for Platform errors, service cancellation before 
            review completion, or duplicate payments.
          </p>
          <p style={pStyle}>
            <strong>No refunds for:</strong> Rejected certifications, changed mind after submission, 
            dissatisfaction with scores, or revoked certifications.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>10. Disclaimers</h2>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>⚠️</span> IMPORTANT: PLEASE READ CAREFULLY
            </div>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '12px'}}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE PLATFORM AND ALL SERVICES ARE PROVIDED "AS IS" AND 
              "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND.
            </p>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}>❌ No warranty of merchantability or fitness for a particular purpose</li>
              <li style={liStyle}>❌ No warranty of accuracy, reliability, or completeness of certification results</li>
              <li style={liStyle}>❌ No warranty that the Service will be uninterrupted, secure, or error-free</li>
              <li style={liStyle}>❌ No guarantee of certification approval or specific ESG scores</li>
              <li style={liStyle}>❌ No endorsement of companies, products, or services</li>
              <li style={liStyle}>❌ No responsibility for third-party reliance on certification data</li>
            </ul>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>11. Limitation of Liability</h2>
          <div style={warningBoxStyle}>
            <div style={warningTitleStyle}>
              <span>⚠️</span> CRITICAL: THIS SECTION LIMITS APPLETREE'S LIABILITY
            </div>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '12px'}}>
              AppleTree's total aggregate liability to you for all claims shall be limited to the GREATER OF:
            </p>
            <ul style={ulStyle}>
              <li style={liStyle}><strong>€100 (one hundred euros)</strong>, OR</li>
              <li style={liStyle}>The <strong>total amount paid by you</strong> to AppleTree in the 12 months preceding the claim</li>
            </ul>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '12px', marginTop: '16px'}}>
              APPLETREE SHALL NOT BE LIABLE FOR:
            </p>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}>❌ Indirect, incidental, special, consequential, or punitive damages</li>
              <li style={liStyle}>❌ Loss of profits, revenue, business opportunities, or goodwill</li>
              <li style={liStyle}>❌ Loss of data or confidential information</li>
              <li style={liStyle}>❌ Reputational harm or business interruption</li>
              <li style={liStyle}>❌ Damages from third-party reliance on certification data</li>
            </ul>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>12. Indemnification</h2>
          <p style={pStyle}>
            You agree to <strong>indemnify, defend, and hold harmless</strong> AppleTree, its founder, 
            future incorporated entity, and affiliates from any claims, liabilities, damages, or costs arising from:
          </p>
          <ul style={ulStyle}>
            <li style={liStyle}>Your use of the Platform (misuse, prohibited activities)</li>
            <li style={liStyle}>Your submitted information and documents (inaccuracy, fraud, infringement)</li>
            <li style={liStyle}>Your breach of these Terms or violation of laws</li>
            <li style={liStyle}>Your violation of third-party rights (intellectual property, privacy)</li>
            <li style={liStyle}>Your ESG claims or conduct (greenwashing, violations)</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>13. Dispute Resolution</h2>
          <h3 style={h3Style}>13.1 Informal Resolution (First Step)</h3>
          <p style={pStyle}>
            Before filing any formal claim, you agree to contact AppleTree at legal@appletreetoken.com 
            and negotiate in good faith for at least 30 days.
          </p>

          <h3 style={h3Style}>13.2 Governing Law</h3>
          <div style={infoBoxStyle}>
            <p style={{...pStyle, marginBottom: '8px'}}>
              <strong>During Pre-Incorporation Phase:</strong>
            </p>
            <ul style={ulStyle}>
              <li style={liStyle}>Governed by <strong>Italian law</strong></li>
              <li style={liStyle}>Exclusive jurisdiction: Competent courts of Rome, Italy</li>
            </ul>
            <p style={{...pStyle, marginBottom: '8px', marginTop: '16px'}}>
              <strong>After Incorporation (Planned):</strong>
            </p>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}>Governed by <strong>Estonian law</strong></li>
              <li style={liStyle}>Exclusive jurisdiction: Competent courts of Tallinn, Estonia</li>
            </ul>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>14. Contact Information</h2>
          <div style={infoBoxStyle}>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '12px'}}>Contact Us:</p>
            <p style={{...pStyle, marginBottom: '8px'}}><strong>Main Email:</strong> info@appletreetoken.com</p>
            <p style={{...pStyle, marginBottom: '8px', fontSize: '12px', color: '#94a3b8'}}>All addresses below are aliases forwarded to info@appletreetoken.com:</p>
            <p style={{...pStyle, marginBottom: '8px'}}><strong>General Support:</strong> support@appletreetoken.com</p>
            <p style={{...pStyle, marginBottom: '8px'}}><strong>Legal & Terms:</strong> legal@appletreetoken.com</p>
            <p style={{...pStyle, marginBottom: '8px'}}><strong>Privacy & Data:</strong> privacy@appletreetoken.com</p>
            <p style={{...pStyle, marginBottom: 0}}><strong>Security Issues:</strong> security@appletreetoken.com</p>
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>15. Acknowledgment</h2>
          <div style={successBoxStyle}>
            <p style={{...pStyle, fontWeight: '600', marginBottom: '12px'}}>
              BY USING THE APPLETREE PLATFORM, YOU ACKNOWLEDGE:
            </p>
            <ul style={{...ulStyle, marginBottom: 0}}>
              <li style={liStyle}>✅ You have read and understood these Terms in their entirety</li>
              <li style={liStyle}>✅ You agree to be legally bound by all provisions</li>
              <li style={liStyle}>✅ You are an Authorized Representative with authority to bind your Company</li>
              <li style={liStyle}>✅ You accept the risks of blockchain and IPFS technology</li>
              <li style={liStyle}>✅ You understand the disclaimers and liability limitations</li>
              <li style={liStyle}>✅ You consent to public disclosure if certified</li>
            </ul>
          </div>
          <p style={{...pStyle, textAlign: 'center', fontWeight: '600', fontSize: '18px', color: '#fca5a5'}}>
            IF YOU DO NOT AGREE, YOU MAY NOT USE THE PLATFORM.
          </p>
        </section>

        <div style={{textAlign: 'center', marginTop: '40px', paddingTop: '30px', borderTop: '2px solid rgba(148, 163, 184, 0.2)'}}>
          <p style={{...pStyle, fontSize: '14px', color: '#94a3b8', marginBottom: '8px'}}>
            <strong>Last Reviewed:</strong> January 20, 2026
          </p>
          <p style={{...pStyle, fontSize: '14px', color: '#94a3b8', marginBottom: '20px'}}>
            <strong>Next Scheduled Review:</strong> July 20, 2026
          </p>
          <button 
            style={buttonStyle}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
            onClick={() => navigate(-1)}
          >
            ← Back to Platform
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
